import React from 'react';
import { MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { STATE_LIST, getDistricts } from '../lib/indiaGeo';

/**
 * Shared address block: State + District (auto-filtered) + City + Street/Flat + Pincode + Landmark.
 * Controlled component — parent owns the `address` object & setter.
 *
 * Required fields: state, district, pincode.
 * The component does NOT enforce required-validation itself, parent does
 * (so it can compose validation with the rest of the form).
 */
export default function AddressFields({ address, setAddress, idPrefix = 'addr', compact = false }) {
  const update = (patch) => setAddress((prev) => ({ ...prev, ...patch }));
  const districts = address?.state ? getDistricts(address.state) : [];

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-block`}>
      <div className="flex items-center gap-2">
        <MapPin size={16} className="text-emerald-600" />
        <Label className="text-sm font-semibold text-gray-800">
          Service Address <span className="text-red-500">*</span>
        </Label>
      </div>

      {/* State + District grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">State *</Label>
          <select
            value={address?.state || ''}
            onChange={(e) => update({ state: e.target.value, district: '' })}
            className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
            data-testid={`${idPrefix}-state`}
          >
            <option value="">— Select State —</option>
            {STATE_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">District *</Label>
          <select
            value={address?.district || ''}
            onChange={(e) => update({ district: e.target.value })}
            disabled={!address?.state}
            className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
            data-testid={`${idPrefix}-district`}
          >
            <option value="">
              {address?.state ? '— Select District —' : 'Pick state first'}
            </option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* City + Pincode */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">City / Town</Label>
          <Input
            value={address?.city || ''}
            onChange={(e) => update({ city: e.target.value })}
            placeholder="e.g., Dehradun"
            className="mt-1 h-10 text-sm"
            data-testid={`${idPrefix}-city`}
          />
        </div>
        <div>
          <Label className="text-xs">Pincode (Optional)</Label>
          <Input
            type="tel"
            inputMode="numeric"
            value={address?.pincode || ''}
            onChange={(e) => update({ pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
            placeholder="6-digit pincode"
            maxLength={6}
            className="mt-1 h-10 text-sm"
            data-testid={`${idPrefix}-pincode`}
          />
        </div>
      </div>

      {/* Street / Flat / Road */}
      <div>
        <Label className="text-xs">House / Flat / Street</Label>
        <Input
          value={address?.line1 || ''}
          onChange={(e) => update({ line1: e.target.value })}
          placeholder="e.g., Flat 304, Tower B, Green Apartments, MG Road"
          className="mt-1 h-10 text-sm"
          data-testid={`${idPrefix}-line1`}
        />
      </div>

      {/* Landmark */}
      {!compact && (
        <div>
          <Label className="text-xs">Landmark (Optional)</Label>
          <Input
            value={address?.landmark || ''}
            onChange={(e) => update({ landmark: e.target.value })}
            placeholder="e.g., Opposite SBI ATM, near City Mall"
            className="mt-1 h-10 text-sm"
            data-testid={`${idPrefix}-landmark`}
          />
        </div>
      )}
    </div>
  );
}

// Helper: validate min required fields.
// Pincode is TRULY OPTIONAL — if provided it must be 6 digits, otherwise empty
// is accepted. Only state + district are required.
export const isAddressValid = (a) => {
  if (!a || !a.state || !a.district) return false;
  if (a.pincode && !/^\d{6}$/.test(a.pincode)) return false; // bad format → invalid
  return true;
};

// Helper: format address dict into a single human-readable string.
export const formatAddress = (a) => {
  if (!a) return '';
  const parts = [a.line1, a.landmark, a.city, a.district, a.state, a.pincode].filter(Boolean);
  return parts.join(', ');
};

export const EMPTY_ADDRESS = {
  state: '',
  district: '',
  city: '',
  line1: '',
  pincode: '',
  landmark: '',
};
